// Updated CRMApiClient.ts - Add includeDetails parameter support
import SkTechApiClient from "./base/SkTechApiClient";
import { parseContentDispositionFilename } from "../helpers/downloadBlob";

export interface Address {
  id: number;
  locationName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  country?: string;
  state?: string;
  zipCode?: string;
  isPrimaryAddress?: boolean;
  latitude?: number;
  longitude?: number;
  plusCode?: string;
  externalCompanyId: number;
  companyId: number;
}

export interface AddressDto {
  locationName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  country?: string;
  state?: string;
  zipCode?: string;
  isPrimaryAddress?: boolean;
  latitude?: number;
  longitude?: number;
  plusCode?: string;
}

/** Result of POST /api/Address/parse: a pasted free-text address split into fields, plus coordinates when found. */
export interface AddressParseResult {
  country: string;
  state: string;
  city: string;
  addressLine1: string;
  zipCode: string;
  plusCode: string;
  locationName: string;
  latitude?: number;
  longitude?: number;
  coordinatesAccuracy: '' | 'exact' | 'approximate';
  formattedAddress: string;
}

export type CrmEntityType = 'ExternalCompany' | 'Contact' | 'Event';

/** Filled by the server for companies, contacts and events. Missing when the record predates auditing. */
export interface CrmAuditFields {
  createdAt?: string;
  createdByUserId?: number;
  createdByName?: string;
  updatedAt?: string;
  updatedByUserId?: number;
  updatedByName?: string;
}

export interface CrmFieldChange {
  field: string;
  oldValue?: string;
  newValue?: string;
}

export type CrmChangeAction = 'Created' | 'Updated' | 'Deleted' | 'AttachmentAdded' | 'AttachmentRemoved';

export interface CrmChangeLog {
  id: number;
  version: number;
  action: CrmChangeAction;
  userId?: number;
  userName?: string;
  createdAt: string;
  changes: CrmFieldChange[];
}

export interface CrmAttachment {
  id: number;
  entityType: number;
  entityId: number;
  fileName: string;
  mimeType?: string;
  sizeBytes: number;
  secureUrl: string;
  resourceType: string;
  uploadedAt: string;
  uploadedByUserId?: number;
  uploadedByName?: string;
}

/** Upload outcome; `code` is an i18n key: FileAlreadyExists | FileTypeNotAllowed | FileTooLarge | FileEmpty | ParentNotFound | UploadFailed. */
export type CrmUploadResult =
  | { ok: true; attachment: CrmAttachment }
  | { ok: false; code: string };

export type CrmReportEntity = 'Companies' | 'Contacts' | 'Events';
export type CrmReportDimension = 'User' | 'Region' | 'Period' | 'Category' | 'EventType' | 'ClientRole' | 'Country' | 'City';
export type CrmPeriodBucket = 'Day' | 'Week' | 'Month' | 'Quarter' | 'Year';

export interface CrmReportRequest {
  entity: CrmReportEntity;
  groupBy: CrmReportDimension;
  secondaryGroupBy?: CrmReportDimension;
  bucket?: CrmPeriodBucket;
  dateFrom?: string;
  dateTo?: string;
  userIds?: number[];
  regionIds?: number[];
  eventTypeIds?: number[];
  countries?: string[];
  cities?: string[];
  categoryIds?: number[];
  timeZoneOffsetMinutes?: number;
}

export interface CrmReportRow {
  key: string;
  label: string;
  secondaryKey?: string;
  secondaryLabel?: string;
  count: number;
}

export interface CrmReportResult {
  entity: string;
  groupBy: string;
  secondaryGroupBy?: string;
  bucket: string;
  rows: CrmReportRow[];
  total: number;
}

export interface CrmReportExportRequest extends CrmReportRequest {
  language?: string;
  title?: string;
  chartImageBase64?: string;
}

export interface Category { id: number; name: string; description?: string; companyId: number; }

export interface Region { id: number; name: string; companyId: number; }
export interface RegionDto { name: string; }

export interface ExternalCompany extends CrmAuditFields {
  id: number;
  name: string;
  edrpou?: string;
  description?: string;
  categoryId?: number;
  category?: Category;
  regionId?: number;
  region?: Region;
  companyId: number;
  addresses?: Address[];
  events?: CrmEvent[];
  contacts?: Contact[];
}

export interface Contact extends CrmAuditFields {
  id: number;
  name: string;
  surname: string;
  phoneNumber?: string;
  email?: string;
  roleId?: number;
  externalCompanyId?: number;
  companyId: number;
  role?: ClientRole;
  externalCompany?: ExternalCompany;
  additionalInfos?: ContactAdditionalInfo[];
  events?: CrmEvent[];
}

export interface ContactAdditionalInfo {
  id: number;
  contactId: number;
  type: string;
  name: string;
}

export interface ClientRole { id: number; name: string; companyId: number; }
export interface ContactType { id: number; name: string; validationKind?: string; companyId: number; }
export interface CrmEventType { id: number; name: string; companyId: number; }

export interface ContactAdditionalInfoDto {
  type: string;
  name: string;
}

export interface ContactDto {
  name: string;
  surname: string;
  phoneNumber?: string;
  email?: string;
  roleId?: number;
  externalCompanyId?: number;
  additionalInfos?: ContactAdditionalInfoDto[];
}

export interface ClientRoleDto { name: string; }
export interface ContactTypeDto { name: string; validationKind?: string; }
export interface CrmEventTypeDto { name: string; }
export interface CategoryDto { name: string; description?: string; }

export interface ExternalCompanyDto {
  name: string;
  edrpou?: string;
  description?: string;
  categoryId?: number;
  regionId?: number;
  addresses?: AddressDto[];
}

export interface CrmEvent extends CrmAuditFields {
  id: number;
  dateStart: string;
  dateEnd: string;
  typeId: number;
  regionId?: number;
  region?: Region;
  contacts?: Contact[];
  externalCompanies?: ExternalCompany[];
  name: string;
  description?: string;
  companyId: number;
  type?: CrmEventType;
}

export interface CrmEventDto {
  dateStart: string;
  dateEnd: string;
  typeId: number;
  regionId?: number;
  contactIds?: number[];
  externalCompanyIds?: number[];
  name: string;
  description?: string;
}

export default class CRMApiClient extends SkTechApiClient {
  // UPDATED: Contacts with performance optimization
  getContacts = async (includeDetails = false): Promise<Contact[]> => {
    const url = includeDetails ? "/api/Contact?includeDetails=true" : "/api/Contact";
    return this.Get(url).then(r => (r?.status === 200 ? r.data : [])).catch(() => []);
  };

  getContactsByExternalCompany = async (externalCompanyId: number): Promise<Contact[]> =>
    this.Get(`/api/Contact/company/${externalCompanyId}`).then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  getContactById = async (id: number): Promise<Contact | null> =>
    this.Get(`/api/Contact/${id}`).then(r => (r?.status === 200 ? r.data : null)).catch(() => null);

  createContact = async (dto: ContactDto): Promise<Contact | null> => {
    try {
      const r = await this.Post("/api/Contact", dto);
      return (r?.status === 200 || r?.status === 201) ? r.data : null;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data || err?.message;
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  updateContact = async (id: number, dto: ContactDto): Promise<boolean> => {
    try {
      const r = await this.Put(`/api/Contact/${id}`, dto);
      return r?.status === 200 || r?.status === 204;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data || err?.message;
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  deleteContact = async (id: number): Promise<boolean> =>
    this.Delete(`/api/Contact/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  connectGoogleContacts = async (code: string, redirectUri?: string): Promise<{ connected?: boolean; email?: string; enableGoogleContactsSync?: boolean; error?: string }> =>
    this.Post("/api/Contact/google/connect", { code, redirectUri })
      .then(r => (r?.status === 200 ? r.data : { error: typeof r?.data === 'string' ? r.data : 'Failed to connect Google account' }))
      .catch((err) => ({ error: typeof err?.response?.data === 'string' ? err?.response?.data : (err?.message || 'Server error') }));

  disconnectGoogleContacts = async (): Promise<{ disconnected?: boolean; error?: string }> =>
    this.Post("/api/Contact/google/disconnect", {})
      .then(r => (r?.status === 200 ? r.data : { error: typeof r?.data === 'string' ? r.data : 'Failed to disconnect Google account' }))
      .catch((err) => ({ error: typeof err?.response?.data === 'string' ? err?.response?.data : (err?.message || 'Server error') }));

  syncGoogleContacts = async (accessToken?: string): Promise<{ syncedCount?: number; error?: string }> =>
    this.Post("/api/Contact/sync-google", { accessToken })
      .then(r => (r?.status === 200 ? r.data : { error: typeof r?.data === 'string' ? r.data : 'Failed to sync' }))
      .catch((err) => ({ error: typeof err?.response?.data === 'string' ? err?.response?.data : (err?.message || 'Server error') }));

  syncSingleContactToGoogle = async (id: number, accessToken?: string): Promise<{ resourceName?: string; error?: string }> =>
    this.Post(`/api/Contact/${id}/sync-google`, { accessToken })
      .then(r => (r?.status === 200 ? r.data : { error: typeof r?.data === 'string' ? r.data : 'Failed to sync' }))
      .catch((err) => ({ error: typeof err?.response?.data === 'string' ? err?.response?.data : (err?.message || 'Server error') }));

  getClientRoles = async (): Promise<ClientRole[]> =>
    this.Get("/api/ClientRole").then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  getClientRoleById = async (id: number): Promise<ClientRole | null> =>
    this.Get(`/api/ClientRole/${id}`).then(r => (r?.status === 200 ? r.data : null)).catch(() => null);

  createClientRole = async (dto: ClientRoleDto): Promise<ClientRole | null> =>
    this.Post("/api/ClientRole", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null)).catch(() => null);

  updateClientRole = async (id: number, dto: ClientRoleDto): Promise<boolean> =>
    this.Put(`/api/ClientRole/${id}`, dto).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  deleteClientRole = async (id: number): Promise<boolean> =>
    this.Delete(`/api/ClientRole/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  getContactTypes = async (): Promise<ContactType[]> =>
    this.Get("/api/ContactType").then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  createContactType = async (dto: ContactTypeDto): Promise<ContactType | null> =>
    this.Post("/api/ContactType", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null)).catch(() => null);

  updateContactType = async (id: number, dto: ContactTypeDto): Promise<boolean> =>
    this.Put(`/api/ContactType/${id}`, dto).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  deleteContactType = async (id: number): Promise<boolean> =>
    this.Delete(`/api/ContactType/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  getCrmEventTypes = async (): Promise<CrmEventType[]> =>
    this.Get("/api/CrmEventType").then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  createCrmEventType = async (dto: CrmEventTypeDto): Promise<CrmEventType | null> =>
    this.Post("/api/CrmEventType", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null)).catch(() => null);

  updateCrmEventType = async (id: number, dto: CrmEventTypeDto): Promise<boolean> =>
    this.Put(`/api/CrmEventType/${id}`, dto).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  deleteCrmEventType = async (id: number): Promise<boolean> =>
    this.Delete(`/api/CrmEventType/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  getCategories = async (): Promise<Category[]> =>
    this.Get("/api/Category").then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  getCategoryById = async (id: number): Promise<Category | null> =>
    this.Get(`/api/Category/${id}`).then(r => (r?.status === 200 ? r.data : null)).catch(() => null);

  createCategory = async (dto: CategoryDto): Promise<Category | null> =>
    this.Post("/api/Category", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null)).catch(() => null);

  updateCategory = async (id: number, dto: CategoryDto): Promise<boolean> =>
    this.Put(`/api/Category/${id}`, dto).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  deleteCategory = async (id: number): Promise<boolean> =>
    this.Delete(`/api/Category/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  getRegions = async (): Promise<Region[]> =>
    this.Get("/api/Region").then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  createRegion = async (dto: RegionDto): Promise<Region | null> =>
    this.Post("/api/Region", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null)).catch(() => null);

  updateRegion = async (id: number, dto: RegionDto): Promise<boolean> =>
    this.Put(`/api/Region/${id}`, dto).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  deleteRegion = async (id: number): Promise<boolean> =>
    this.Delete(`/api/Region/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  getExternalCompanies = async (): Promise<ExternalCompany[]> =>
    this.Get("/api/ExternalCompany").then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  getExternalCompanyById = async (id: number): Promise<ExternalCompany | null> =>
    this.Get(`/api/ExternalCompany/${id}`).then(r => (r?.status === 200 ? r.data : null)).catch(() => null);

  createExternalCompany = async (dto: ExternalCompanyDto): Promise<ExternalCompany | null> =>
    this.Post("/api/ExternalCompany", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null)).catch(() => null);

  updateExternalCompany = async (id: number, dto: ExternalCompanyDto): Promise<boolean> =>
    this.Put(`/api/ExternalCompany/${id}`, dto).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  deleteExternalCompany = async (id: number): Promise<boolean> =>
    this.Delete(`/api/ExternalCompany/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  // UPDATED: CRM Events with performance optimization
  getCrmEvents = async (includeDetails = false): Promise<CrmEvent[]> => {
    const url = includeDetails ? "/api/CrmEvent?includeDetails=true" : "/api/CrmEvent";
    return this.Get(url).then(r => (r?.status === 200 ? r.data : [])).catch(() => []);
  };

  getCrmEventById = async (id: number): Promise<CrmEvent | null> =>
    this.Get(`/api/CrmEvent/${id}`).then(r => (r?.status === 200 ? r.data : null)).catch(() => null);

  createCrmEvent = async (dto: CrmEventDto): Promise<CrmEvent | null> =>
    this.Post("/api/CrmEvent", dto)
      .then(r => {
        if (r?.status === 200 || r?.status === 201) return r.data;
        console.error("createCrmEvent unexpected status:", r?.status, r?.data);
        return null;
      })
      .catch(err => {
        console.error("createCrmEvent request failed:", err);
        return null;
      });

  updateCrmEvent = async (id: number, dto: CrmEventDto): Promise<boolean> =>
    this.Put(`/api/CrmEvent/${id}`, dto).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  deleteCrmEvent = async (id: number): Promise<boolean> =>
    this.Delete(`/api/CrmEvent/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  getCrmEventsByContact = async (contactId: number): Promise<CrmEvent[]> =>
    this.Get(`/api/CrmEvent/contact/${contactId}`).then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  getCrmEventsByExternalCompany = async (companyId: number): Promise<CrmEvent[]> =>
    this.Get(`/api/CrmEvent/company/${companyId}`).then(r => (r?.status === 200 ? r.data : [])).catch(() => []);

  getCrmHistory = async (entityType: CrmEntityType, entityId: number): Promise<CrmChangeLog[]> =>
    this.Get(`/api/CrmHistory/${entityType}/${entityId}`)
      .then(r => (r?.status === 200 ? (r.data as CrmChangeLog[]).map(row => ({ ...row, changes: row.changes ?? [] })) : []))
      .catch(() => []);

  getCrmAttachments = async (entityType: CrmEntityType, entityId: number): Promise<CrmAttachment[]> =>
    this.Get(`/api/CrmAttachment/${entityType}/${entityId}`)
      .then(r => (r?.status === 200 ? (r.data as CrmAttachment[]) : []))
      .catch(() => []);

  uploadCrmAttachment = async (entityType: CrmEntityType, entityId: number, file: File): Promise<CrmUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await this.Post(`/api/CrmAttachment/${entityType}/${entityId}`, formData);
      if (r?.status === 201 || r?.status === 200) return { ok: true, attachment: r.data as CrmAttachment };
      return { ok: false, code: 'UploadFailed' };
    } catch (err: any) {
      const code = err?.response?.data?.code;
      return { ok: false, code: typeof code === 'string' ? code : 'UploadFailed' };
    }
  };

  deleteCrmAttachment = async (id: number): Promise<boolean> =>
    this.Delete(`/api/CrmAttachment/${id}`).then(r => r?.status === 200 || r?.status === 204).catch(() => false);

  /** AI address parsing; returns the parsed fields or throws the server error code ('EmptyText', 'AiUnavailable', ...). */
  parseAddress = async (text: string): Promise<AddressParseResult> => {
    try {
      const r = await this.Post('/api/Address/parse', { text });
      return r.data as AddressParseResult;
    } catch (err: any) {
      throw new Error(err?.response?.data?.code || 'AddressParseFailed');
    }
  };

  aggregateCrmReport = async (request: CrmReportRequest): Promise<CrmReportResult | null> =>
    this.Post('/api/CrmReport/aggregate', request)
      .then(r => (r?.status === 200 ? { ...(r.data as CrmReportResult), rows: (r.data as CrmReportResult).rows ?? [] } : null))
      .catch(() => null);

  /** Returns the file blob and the server-provided file name (Content-Disposition), or null. */
  exportCrmReport = async (request: CrmReportExportRequest, format: 'xlsx' | 'pdf'): Promise<{ blob: Blob; fileName: string } | null> => {
    try {
      const r = await this.PostBlob(`/api/CrmReport/export?format=${format}`, request);
      if (r.status !== 200) return null;
      const fallback = `crm-report.${format}`;
      return { blob: r.data as Blob, fileName: parseContentDispositionFilename(r.headers['content-disposition'], fallback) };
    } catch {
      return null;
    }
  };
}
